import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import * as authService from 'services/authService/authService';
import { AuthSession } from 'models/auth';
import { ApiError } from 'models/errors/ApiError';
import { DiamUser } from 'models/user';
import { setCustomer } from 'store/customerState';
import { diamUserIdStorageKey } from 'utils/constants';

export type AuthState = {
  authCheck: boolean;
  hasLoggedIn: boolean;
  session: AuthSession;
  isLoading: boolean;
  error: ApiError | null;
};

export const defaultAuthState: AuthState = {
  authCheck: false,
  hasLoggedIn: false,
  session: {
    authenticated: false,
    user: null
  },
  isLoading: false,
  error: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: defaultAuthState,
  reducers: {
    authCheck: (state) => {
      state.authCheck = true;
    },
    logoutStart: (state) => {
      state.isLoading = true;
    },
    resetHasLoggedIn: (state) => {
      state.hasLoggedIn = false;
    },
    logoutSuccess: (state) => {
      state.isLoading = false;
      state.error = null;
      state.session = defaultAuthState.session;
    },
    validateTokenStart: (state) => {
      state.isLoading = true;
    },
    validateTokenSuccess: (state, action: PayloadAction<DiamUser>) => {
      state.session.user = action.payload;
      state.session.authenticated = true;
      state.hasLoggedIn = true;
      state.isLoading = false;
      state.error = null;
    },
    validateTokenFail: (state, action: PayloadAction<ApiError>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    refreshTokenSuccess: (state) => {
      state.session.authenticated = true;
    }
  }
});

// Export actions
export const {
  authCheck,
  resetHasLoggedIn,
  logoutStart,
  logoutSuccess,
  validateTokenStart,
  validateTokenSuccess,
  validateTokenFail,
  refreshTokenSuccess
} = authSlice.actions;

// Export reducer
export const authReducer = authSlice.reducer;

// Export selector
export const authSelector = (state: RootState) => state.auth;

// Export thunks
export function checkSession() {
  return async (dispatch: Dispatch) => {
    dispatch(validateTokenStart());
    try {
      const response = await authService.getUserInfo();
      dispatch(validateTokenSuccess(response.data));
      dispatch(setCustomer(response.included[0]));
      sessionStorage.setItem(diamUserIdStorageKey, response.data.id);
    } catch (e: any) {
      sessionStorage.removeItem(diamUserIdStorageKey);
      dispatch(validateTokenFail(e));
      dispatch(logoutSuccess());
    }
    dispatch(authCheck());
  };
}

export function logout() {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(logoutStart());
      await authService.logout();
    } catch {
      // Do nothing
    } finally {
      sessionStorage.removeItem(diamUserIdStorageKey);
      dispatch(logoutSuccess());
      dispatch(authCheck());
    }
  };
}
